import { batch, createMemo, Show } from 'solid-js'
import { activeTool, setStyle, style } from '../editor.ts'
import {
  beginCommit,
  commit,
  endCommit,
  isCommitOpen,
  scene,
  selectedNodes,
  setBackground,
  updateNode,
} from '../scene/store.ts'
import { CANVAS_PALETTE, ColorTabs, INK_PALETTE, type ColorTarget } from './ColorField.tsx'
import type { NodeType, SceneNode, TextAlign } from '../scene/types.ts'

function applyToSelection(patch: Partial<SceneNode>, label: string): void {
  const nodes = selectedNodes()
  if (nodes.length === 0) return
  commit(label, nodes.map((n) => n.id), () => {
    for (const n of nodes) updateNode(n.id, patch)
  })
}

/**
 * Slider drags write through on every input so the canvas follows the thumb;
 * the commit opened on the first input closes on release as one undo step.
 */
function previewOnSelection(patch: Partial<SceneNode>): void {
  const nodes = selectedNodes()
  if (nodes.length === 0) return
  if (!isCommitOpen()) beginCommit(nodes.map((n) => n.id))
  for (const n of nodes) updateNode(n.id, patch)
}

function finishOnSelection(patch: Partial<SceneNode>, label: string): void {
  if (selectedNodes().length === 0) return
  previewOnSelection(patch)
  endCommit(label)
}

/** First value of a property across the selection, for showing in an input. */
function shared<T>(pick: (n: SceneNode) => T | undefined, fallback: T): T {
  for (const n of selectedNodes()) {
    const value = pick(n)
    if (value !== undefined) return value
  }
  return fallback
}

export function StylePanel() {
  /**
   * A whole group in the selection hides the style controls: one color across a
   * group's mixed members is rarely wanted. Members picked out individually,
   * from the layers panel, still get them.
   */
  const wholeGroupSelected = createMemo(() => {
    const sel = selectedNodes()
    const ids = new Set(sel.map((n) => n.id))
    const groups = new Set(sel.map((n) => n.groupId).filter((g) => g))
    return [...groups].some((g) =>
      Object.values(scene.nodes).every((n) => n.groupId !== g || ids.has(n.id)),
    )
  })
  const types = createMemo(
    () => new Set<NodeType>(wholeGroupSelected() ? [] : selectedNodes().map((n) => n.type)),
  )
  const empty = () => selectedNodes().length === 0

  // With nothing selected the panel edits the defaults for the active tool.
  const hasClosedLine = () =>
    !wholeGroupSelected() && selectedNodes().some((n) => n.type === 'line' && n.closed)
  // The line tool shows Fill too: it applies when a chain is closed.
  const showFill = () =>
    empty()
      ? activeTool() === 'rect' || activeTool() === 'ellipse' || activeTool() === 'line'
      : types().has('rect') || types().has('ellipse') || hasClosedLine()
  const showStroke = () =>
    empty()
      ? activeTool() === 'rect' || activeTool() === 'ellipse' || activeTool() === 'line'
      : types().has('rect') || types().has('ellipse') || types().has('line')
  const showCorner = () => (empty() ? activeTool() === 'rect' : types().has('rect'))
  const showInk = () => (empty() ? activeTool() === 'pen' : types().has('path'))
  const showText = () => (empty() ? activeTool() === 'text' : types().has('text'))
  // Colors carry their own alpha; images have no color, so they keep a slider.
  const showOpacity = () => !empty() && types().has('image')

  const anything = () =>
    showFill() || showStroke() || showCorner() || showInk() || showText() || showOpacity()

  /** A color property that writes the tool default and, live, the selection. */
  const nodeColor = (
    key: string,
    label: string,
    styleKey: 'fill' | 'stroke' | 'inkColor' | 'textColor',
    prop: 'fill' | 'stroke' | 'color',
    pick: (n: SceneNode) => string | undefined,
    undoLabel: string,
  ): ColorTarget => ({
    key,
    label,
    get value() {
      return shared(pick, style[styleKey])
    },
    palette: INK_PALETTE,
    onInput: (color) =>
      batch(() => {
        setStyle(styleKey, color)
        previewOnSelection({ [prop]: color } as Partial<SceneNode>)
      }),
    onChange: (color) =>
      batch(() => {
        setStyle(styleKey, color)
        finishOnSelection({ [prop]: color } as Partial<SceneNode>, undoLabel)
      }),
  })

  const canvasTarget: ColorTarget = {
    key: 'canvas',
    label: 'Canvas',
    get value() {
      return scene.background
    },
    palette: CANVAS_PALETTE,
    onInput: setBackground,
    onChange: setBackground,
  }
  const fillTarget = nodeColor('fill', 'Fill', 'fill', 'fill', (n) => ('fill' in n ? n.fill : undefined), 'Fill')
  const strokeTarget = nodeColor('stroke', 'Stroke', 'stroke', 'stroke', (n) => (n.type !== 'path' && 'stroke' in n ? n.stroke : undefined), 'Stroke')
  const inkTarget = nodeColor('ink', 'Ink', 'inkColor', 'stroke', (n) => (n.type === 'path' ? n.stroke : undefined), 'Ink color')
  const textTarget = nodeColor('text', 'Text', 'textColor', 'color', (n) => (n.type === 'text' ? n.color : undefined), 'Text color')

  const targets = createMemo(() => [
    ...(empty() ? [canvasTarget] : []),
    ...(showFill() ? [fillTarget] : []),
    ...(showStroke() ? [strokeTarget] : []),
    ...(showInk() ? [inkTarget] : []),
    ...(showText() ? [textTarget] : []),
  ])

  return (
    <div class="style-bar" title={empty() ? 'Defaults for new objects' : undefined}>
      <ColorTabs targets={targets()} />

      <Show when={anything()}>
        <Show when={showStroke()}>
          <label class="row">
            <span>Width</span>
            <input
              type="range"
              min="0"
              max="40"
              step="1"
              value={shared((n) => ('strokeWidth' in n ? n.strokeWidth : undefined), style.strokeWidth)}
              onInput={(e) => {
                const v = e.currentTarget.valueAsNumber
                batch(() => {
                  setStyle('strokeWidth', v)
                  previewOnSelection({ strokeWidth: v } as Partial<SceneNode>)
                })
              }}
              onChange={(e) =>
                finishOnSelection({ strokeWidth: e.currentTarget.valueAsNumber } as Partial<SceneNode>, 'Stroke width')
              }
            />
          </label>
        </Show>

        <Show when={showCorner()}>
          <label class="row">
            <span>Corner</span>
            <input
              type="range"
              min="0"
              max="80"
              step="1"
              value={shared((n) => (n.type === 'rect' ? n.cornerRadius : undefined), style.cornerRadius)}
              onInput={(e) => {
                const v = e.currentTarget.valueAsNumber
                batch(() => {
                  setStyle('cornerRadius', v)
                  previewOnSelection({ cornerRadius: v } as Partial<SceneNode>)
                })
              }}
              onChange={(e) =>
                finishOnSelection({ cornerRadius: e.currentTarget.valueAsNumber } as Partial<SceneNode>, 'Corner radius')
              }
            />
          </label>
        </Show>

        <Show when={showInk()}>
          <label class="row">
            <span>Nib</span>
            <input
              type="range"
              min="1"
              max="40"
              step="1"
              value={shared((n) => (n.type === 'path' ? n.strokeWidth : undefined), style.inkWidth)}
              onInput={(e) => {
                const v = e.currentTarget.valueAsNumber
                batch(() => {
                  setStyle('inkWidth', v)
                  previewOnSelection({ strokeWidth: v } as Partial<SceneNode>)
                })
              }}
              onChange={(e) =>
                finishOnSelection({ strokeWidth: e.currentTarget.valueAsNumber } as Partial<SceneNode>, 'Nib width')
              }
            />
          </label>
        </Show>

        <Show when={showText()}>
          <label class="row">
            <span>Size</span>
            <input
              type="number"
              min="6"
              max="400"
              step="1"
              value={shared((n) => (n.type === 'text' ? n.fontSize : undefined), style.fontSize)}
              onChange={(e) => {
                const v = e.currentTarget.valueAsNumber
                batch(() => {
                  setStyle('fontSize', v)
                  applyToSelection({ fontSize: v } as Partial<SceneNode>, 'Font size')
                })
              }}
            />
          </label>
          <Show when={!empty()}>
            <div class="row">
              <span>Align</span>
              <div class="segmented">
                {(['left', 'center', 'right'] as TextAlign[]).map((align) => (
                  <button
                    type="button"
                    classList={{
                      active: shared((n) => (n.type === 'text' ? n.align : undefined), 'left') === align,
                    }}
                    onClick={() => applyToSelection({ align } as Partial<SceneNode>, 'Align')}
                  >
                    {align[0]!.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </Show>
        </Show>

        <Show when={showOpacity()}>
          <label class="row">
            <span>Opacity</span>
            <input
              type="range"
              min="0.05"
              max="1"
              step="0.05"
              value={shared((n) => n.opacity, 1)}
              onInput={(e) => previewOnSelection({ opacity: e.currentTarget.valueAsNumber })}
              onChange={(e) => finishOnSelection({ opacity: e.currentTarget.valueAsNumber }, 'Opacity')}
            />
          </label>
        </Show>
      </Show>
    </div>
  )
}
